// AUSTIN'S TROPHY ROOM.
//
// The catalogue is CODE, not data. Trophies are a fixed set with a rule each;
// only the EARNING is a stored record. That way adding a trophy in a later
// version does not need a migration, and a trophy whose rule changes does not
// leave a stale copy of the old wording sitting in a database.
//
// Every `test` gets the run that just ended plus lifetime stats, and must be
// cheap — they all run on every single game over.

import { trophys, addTrophy, trophysReady } from '../store.js';

export const TROPHIES = [
  // --- getting started ------------------------------------------------------
  { id: 'austin-your-marks', name: 'Austin Your Marks', icon: '🏁', description: 'Finish your first game.', test: ({ stats }) => stats.runs >= 1 },
  { id: 'full-house', name: 'The Full Austin', icon: '🎰', description: 'Play every single mode at least once.', test: ({ stats }) => stats.modesPlayed >= 6 },

  // --- streaks --------------------------------------------------------------
  { id: 'austinishing', name: 'AUSTINISHING', icon: '⭐', description: 'Get 5 right in a row.', test: ({ run }) => (run.bestChain ?? 0) >= 5 },
  { id: 'un-austin-able', name: 'Un-Austin-able', icon: '🔥', description: 'Get 10 right in a row.', test: ({ run }) => (run.bestChain ?? 0) >= 10 },
  { id: 'austin-on-fire', name: 'AUSTIN ON FIRE', icon: '🌋', description: 'Get 20 right in a row.', test: ({ run }) => (run.bestChain ?? 0) >= 20 },

  // --- endurance ------------------------------------------------------------
  { id: 'exhaustin', name: 'Exhaustin', icon: '😤', description: 'Last a whole minute in Endurance.', test: ({ run }) => run.mode === 'endurance' && run.lastedMs >= 60000 },
  { id: 'really-exhaustin', name: 'Really Exhaustin', icon: '💦', description: 'Last 3 minutes in Endurance.', test: ({ run }) => run.mode === 'endurance' && run.lastedMs >= 180000 },
  { id: 'utterly-exhaustin', name: 'Utterly Exhaustin', icon: '🫠', description: 'Last 5 minutes in Endurance. Nobody is watching, but still.', test: ({ run }) => run.mode === 'endurance' && run.lastedMs >= 300000 },

  // --- the AUS bonus --------------------------------------------------------
  { id: 'name-on-it', name: 'Got Your Name On It', icon: '🇦🇺', description: 'Trigger the AUS BONUS for the first time.', test: ({ stats }) => stats.ausBonuses >= 1 },
  { id: 'aus-some', name: 'Aus-some', icon: '🦌', description: 'Trigger the AUS BONUS 10 times.', test: ({ stats }) => stats.ausBonuses >= 10 },
  { id: 'fully-austinated', name: 'FULLY AUSTINATED', icon: '👑', description: 'Score 1,000 in a single game on the hardest setting.', test: ({ run }) => run.score >= 1000 },

  // --- per mode -------------------------------------------------------------
  { id: 'chain-gang', name: 'Chain Gang', icon: '🔗', description: 'Build a chain 6 links long in Chain Reaction.', test: ({ run }) => run.mode === 'chain-reaction' && (run.bestChain ?? 0) >= 6 },
  { id: 'higher-power', name: 'Higher Power', icon: '⚖️', description: 'Get 12 right in one round of Higher or Lower.', test: ({ run }) => run.mode === 'higher-lower' && (run.correct ?? 0) >= 12 },
  { id: 'odd-job', name: 'Odd Job', icon: '🔍', description: 'Get 8 right in one round of Odd One Out.', test: ({ run }) => run.mode === 'odd-one-out' && (run.correct ?? 0) >= 8 },
  { id: 'last-gasper', name: 'Last Gasper', icon: '💨', description: 'Get 18 right in one 60-second Last Gasp round.', test: ({ run }) => run.mode === 'last-gasp' && (run.correct ?? 0) >= 18 },
  { id: 'early-bird', name: 'Just-in Time', icon: '👁️', description: 'Guess the Thing correctly on the very first clue.', test: ({ run }) => run.mode === 'guess-the-thing' && (run.bestChain ?? 0) >= 1 && run.score >= 400 },
  { id: 'sorted', name: 'Sorted', icon: '📊', description: 'Get a Sort It round perfect.', test: ({ run }) => run.mode === 'sort-it' && (run.correct ?? 0) >= 3 && (run.wrong ?? 0) === 0 },

  // --- coming back ----------------------------------------------------------
  { id: 'daily-first', name: 'Daily Bread', icon: '📅', description: 'Play your first Daily EXHAUSTIN.', test: ({ profile }) => (profile.dailyStreak ?? 0) >= 1 },
  { id: 'daily-week', name: 'Austin Every Day', icon: '📆', description: 'Keep a 7-day Daily streak going.', test: ({ profile }) => (profile.dailyStreak ?? 0) >= 7 },

  // --- the long haul --------------------------------------------------------
  { id: 'ten-thousand', name: 'Austin the Bank', icon: '💰', description: 'Reach 10,000 points in total.', test: ({ profile }) => (profile.totalScore ?? 0) >= 10000 },
  { id: 'century', name: 'Austin the Century', icon: '💯', description: 'Answer 100 questions correctly, all told.', test: ({ stats }) => stats.totalCorrect >= 100 },
];

export const trophyById = (id) => TROPHIES.find((t) => t.id === id) ?? null;

/** Lifetime numbers, computed from the run history rather than counted up. */
export function lifetimeStats(runs = []) {
  const real = runs.filter((r) => Number(r.id) > 0 || r.mode);
  return {
    runs: real.length,
    totalCorrect: real.reduce((n, r) => n + (r.correct ?? 0), 0),
    totalWrong: real.reduce((n, r) => n + (r.wrong ?? 0), 0),
    ausBonuses: real.reduce((n, r) => n + (r.ausBonuses ?? 0), 0),
    modesPlayed: new Set(real.map((r) => r.mode).filter((m) => m && m !== 'endurance' && m !== 'daily')).size,
    bestEndurance: real.filter((r) => r.mode === 'endurance').reduce((n, r) => Math.max(n, r.lastedMs ?? 0), 0),
  };
}

/** Which trophy ids are already in the cabinet. */
export const earnedIds = () => new Set(trophys.value.map((t) => t.criterion));

/**
 * Award anything this run just earned, and return the new ones.
 *
 * `criterion` holds the trophy id — the stored row is only ever a receipt, so
 * the wording can change in a later version without a migration.
 */
export async function awardTrophies({ run, profile, runs }) {
  await trophysReady.catch(() => null);
  const already = earnedIds();
  const stats = lifetimeStats(runs);
  const won = [];

  for (const t of TROPHIES) {
    if (already.has(t.id)) continue;
    let passed = false;
    try {
      passed = !!t.test({ run, profile, runs, stats });
    } catch {
      passed = false;
    }
    if (!passed) continue;
    addTrophy({ name: t.name, description: t.description, criterion: t.id, icon: t.icon });
    won.push(t);
  }
  return won;
}
